/**
 * Hex → horticultural colour name.
 *
 * The catalog stores foliage and bloom colours as hex, which is exactly what a renderer wants and
 * exactly what an image model does not. "#c1350f" carries almost no signal in a prompt; "deep
 * scarlet" carries a great deal. This module is the translation layer, kept pure and small so the
 * naming can be unit-tested rather than eyeballed.
 */

interface HueBand {
  /** Upper bound (exclusive) in degrees. Bands are scanned in order after normalising hue. */
  until: number;
  name: string;
}

/**
 * Hue bands chosen for plants rather than for a colour picker: the green range is subdivided finely
 * because most of the catalog lives there, and the yellow/orange range distinguishes the autumn
 * words a garden designer would actually use.
 *
 * Note the cyan region — a glaucous conifer sits around hue 195, and calling it "cyan" pushes an
 * image model toward a swimming-pool blue. Foliage in that range reads as grey-blue, so that is what
 * it is named. This is a horticultural vocabulary, not a general-purpose colour namer.
 */
const HUE_BANDS: readonly HueBand[] = [
  { until: 10, name: 'red' },
  { until: 20, name: 'scarlet' },
  { until: 32, name: 'burnt orange' },
  { until: 42, name: 'amber' },
  { until: 50, name: 'gold' },
  { until: 60, name: 'straw yellow' },
  { until: 72, name: 'chartreuse' },
  { until: 90, name: 'yellow-green' },
  { until: 150, name: 'green' },
  { until: 175, name: 'blue-green' },
  { until: 200, name: 'grey-blue' },
  { until: 245, name: 'blue' },
  { until: 265, name: 'indigo' },
  { until: 285, name: 'violet' },
  { until: 310, name: 'purple' },
  { until: 330, name: 'magenta' },
  { until: 350, name: 'pink' },
  { until: 360, name: 'red' },
];

export interface Hsl {
  /** 0–360. */
  h: number;
  /** 0–1. */
  s: number;
  /** 0–1. */
  l: number;
}

/** Convert `#rgb` / `#rrggbb` to HSL. Malformed input degrades to black rather than throwing. */
export function hexToHsl(hex: string): Hsl {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.padEnd(6, '0').slice(0, 6);

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return { h: 0, s: 0, l: 0 };

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) return { h: 0, s: 0, l };

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = 60 * (((g - b) / delta) % 6);
  else if (max === g) h = 60 * ((b - r) / delta + 2);
  else h = 60 * ((r - g) / delta + 4);
  if (h < 0) h += 360;

  return { h, s: Math.min(1, Math.max(0, s)), l };
}

function hueName(h: number): string {
  const hue = ((h % 360) + 360) % 360;
  for (const band of HUE_BANDS) {
    if (hue < band.until) return band.name;
  }
  return 'red';
}

/**
 * Brown has no hue of its own — it is a dark, unsaturated orange — so a pure hue lookup calls bark,
 * cones, and winter stems "burnt orange". Since roughly half of what a garden shows in winter is
 * some kind of brown, it gets an explicit rule rather than a wrong word.
 */
function earthName(h: number, s: number, l: number): string | null {
  if (h < 14 || h >= 48) return null;
  if (s >= 0.62) return null;
  if (l < 0.42) return 'brown';
  if (l < 0.62 && s < 0.45) return 'tan';
  return null;
}

function greyName(l: number): string {
  if (l < 0.1) return 'near-black';
  if (l < 0.28) return 'charcoal';
  if (l < 0.48) return 'slate grey';
  if (l < 0.68) return 'grey';
  if (l < 0.86) return 'silver';
  return 'off-white';
}

/**
 * A short colour phrase, e.g. `"deep scarlet"`, `"muted blue-green"`, `"pale straw yellow"`.
 *
 * Lightness supplies the intensity word and low saturation supplies a muting word — the two
 * modifiers a planting plan actually needs to distinguish a glaucous spruce from a glossy holly.
 */
export function describeColor(hex: string): string {
  const { h, s, l } = hexToHsl(hex);
  if (s < 0.1) return greyName(l);

  const earth = earthName(h, s, l);
  const base = earth ?? hueName(h);
  const parts: string[] = [];

  if (l < 0.2) parts.push('deep');
  else if (l < 0.34) parts.push('dark');
  else if (l > 0.82) parts.push('pale');
  else if (l > 0.66) parts.push('light');

  // Saturation reads as vividness. Only call it out at the extremes; mid-range needs no adjective.
  // Brown and tan are already low-saturation words, so "muted brown" would say it twice.
  if (earth === null) {
    if (s < 0.28) parts.push('muted');
    else if (s > 0.85 && l >= 0.2 && l <= 0.66) parts.push('vivid');
  }

  parts.push(base);
  return parts.join(' ');
}
